<script setup lang="ts">
import { computed, ref, shallowRef, watchEffect } from 'vue'
import type { ShallowRef } from 'vue'
import { RadioGroupIndicator, RadioGroupItem, useForwardProps } from 'radix-vue'
import { useEventListener } from '@vueuse/core'
import { VBadge } from '../../VBadge'
import type { IBadgeProps } from '../../VBadge/types'
import { ESelectorItemVariants, type TSelectorItemConfig, type TSelectorItemProps } from '../types'
import useSelectorItemConfig from '../composables/useSelectorItemConfig'
import { SIZES } from '@/ui/ts/types/Sizes'

const props = defineProps<TSelectorItemProps>()

const emits = defineEmits<{
    // no defineModel from VSelector in every VSelectorItem for performance reasons
    (e: 'updateModel', value: string | undefined): void
}>()

const delegatedProps = computed(() => {
    const { class: _, ...delegated } = props

    return delegated
})

const forwardedProps = useForwardProps(delegatedProps)

const selectorItemConfig: ShallowRef<TSelectorItemConfig> = shallowRef({} as Record<string, string>)

watchEffect(() => {
    selectorItemConfig.value = useSelectorItemConfig(props)
})

// focus
const selectorItem = ref<HTMLDivElement | undefined>()

const isBadge = computed<boolean>(() => !!props.badge && props.variant !== ESelectorItemVariants.COMPACT)
const isPlaceholder = computed<boolean>(() => props.isGroupHasAnyBadge && props.variant === ESelectorItemVariants.CHECK)
const isHeaderLabel = computed<boolean>(
    () => !props.isGroupHasAnyBadge && props.variant === ESelectorItemVariants.CHECK,
)
const isRadio = computed<boolean>(() => props.variant === ESelectorItemVariants.CHECK)
const isLabel = computed<boolean>(() => props.variant !== ESelectorItemVariants.CHECK || isPlaceholder.value)
const isParagraph = computed<boolean>(() => !!props.paragraph && props.variant !== ESelectorItemVariants.COMPACT)

useEventListener('keydown', event => {
    if (!props.disabled && selectorItem.value === document.activeElement && event.key === 'Enter') {
        emits('updateModel', props.value)
    }
})

function updateModel(): void {
    if (props.disabled) {
        return
    }
    emits('updateModel', props.value)
}

const badgeOptions: IBadgeProps = {
    dataTest: props.badge?.options?.dataTest ?? `${props.id}_badge`,
    size: props.badge?.options?.size ?? SIZES.XSMALL,
}
</script>

<template>
    <div
        ref="selectorItem"
        :aria-checked="props.isSelected"
        :aria-disabled="props.disabled"
        tabindex="0"
        :class="[selectorItemConfig.root, props.class]"
        @click="updateModel"
    >
        <div :class="selectorItemConfig.headerContainer">
            <slot
                v-if="isBadge"
                name="badge"
                :content="props.badge"
            >
                <VBadge
                    :disabled="props.disabled"
                    v-bind="badgeOptions"
                    data-test="badge_selector"
                >
                    <template #content>
                        <span class="line-clamp-1 overflow-hidden break-all">
                            {{ props.badge?.text ?? '' }}
                        </span>
                    </template>
                </VBadge>
            </slot>
            <div
                v-else-if="isPlaceholder"
                class="size-5"
            />
            <slot
                v-else-if="isHeaderLabel"
                name="label"
                :text="props.label"
                :classes="selectorItemConfig.label"
            >
                <label
                    v-html-gpp="props.label"
                    :for="props.id"
                    :aria-disabled="props.disabled"
                    :class="selectorItemConfig.label"
                />
            </slot>

            <RadioGroupItem
                v-if="isRadio"
                v-bind="forwardedProps"
                :aria-disabled="props.disabled"
                tabindex="-1"
                :class="selectorItemConfig.radio"
            >
                <RadioGroupIndicator :class="selectorItemConfig.radioIndicator">
                    <slot
                        name="indicator"
                        :classes="selectorItemConfig.radioIndicatorIcon"
                    >
                        <VIcon
                            name="check"
                            :size="SIZES.MEDIUM"
                            :aria-disabled="props.disabled"
                            :class="selectorItemConfig.radioIndicatorIcon"
                        />
                    </slot>
                </RadioGroupIndicator>
            </RadioGroupItem>
        </div>

        <slot
            v-if="isLabel"
            name="label"
            :text="props.label"
            :classes="selectorItemConfig.label"
        >
            <label
                v-html-gpp="props.label"
                :for="props.id"
                :aria-disabled="props.disabled"
                :class="selectorItemConfig.label"
            />
        </slot>

        <slot
            v-if="isParagraph"
            name="paragraph"
            :text="props.paragraph"
            :classes="selectorItemConfig.paragraph"
        >
            <p
                v-html-gpp="props.paragraph"
                :aria-disabled="props.disabled"
                :class="selectorItemConfig.paragraph"
            />
        </slot>
    </div>
</template>

<style scoped>
.word-break-word{word-break:break-word}
</style>
